import React, { type ReactNode, useCallback } from 'react';
import { IconButton } from '@atlaskit/button/new';
import EditorCloseIcon from '@atlaskit/icon/core/cross';
import { Box, xcss, Inline } from '@atlaskit/primitives';
import { HelpPanelLink } from '@atlassian/jira-help-panel-button/src/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import {
	BANNER,
	ContextualAnalyticsData,
	FireScreenAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import { useMinimizableModal } from '@atlassian/jira-minimizable-modal/src/controllers/use-minimizable-modal/index.tsx';
import { useSmartFieldsUpdateUserPreference } from '@atlassian/jira-personal-settings-gic-smart-fields/src/services/index.tsx';
import { useIsSmartFieldsOptIn } from '@atlassian/jira-router-resources-gic-smart-fields/src/services/index.tsx';
import messages from './messages.tsx';

export const SmartFieldMessage = () => {
	const { formatMessage } = useIntl();
	const {
		isSmartFieldsOptIn,
		isSmartFieldDismissedReadMore,
		isSmartFieldsGicOnboardingDismissed,
		dismissGicOnboarding,
	} = useIsSmartFieldsOptIn();

	const { updatePreferences } = useSmartFieldsUpdateUserPreference();

	const handleDismissMessage = useCallback(() => {
		dismissGicOnboarding();
		updatePreferences({
			hasUserOptedOut: !isSmartFieldsOptIn,
			hasUserDismissedGicOnboarding: true,
			hasUserDismissedReadMore: !!isSmartFieldDismissedReadMore,
		});
	}, [dismissGicOnboarding, isSmartFieldDismissedReadMore, isSmartFieldsOptIn, updatePreferences]);

	const { view, setView, setShouldFocusChanger } = useMinimizableModal();

	const handleOnReadMoreClick = useCallback(() => {
		if (view === 'modal') {
			setShouldFocusChanger(true);
			setView('mini');
		}
	}, [view, setShouldFocusChanger, setView]);

	return isSmartFieldsOptIn && isSmartFieldsGicOnboardingDismissed !== true ? (
		<ContextualAnalyticsData sourceName="gicSmartField" sourceType={BANNER}>
			<Box backgroundColor="color.background.discovery" padding="space.100" xcss={boxStyles}>
				<Inline spread="space-between">
					<Box xcss={helpTextStyles}>
						{formatMessage(messages.readMore, {
							link: (chunks: ReactNode[]) => (
								<HelpPanelLink
									articleId="3svLIYAFXrcqQlJXtqEc6G"
									appearance="link"
									spacing="none"
									onClick={handleOnReadMoreClick}
								>
									{chunks}
								</HelpPanelLink>
							),
						})}
					</Box>
					<IconButton
						appearance="subtle"
						icon={EditorCloseIcon}
						label={formatMessage(messages.dismissButtonLabel)}
						onClick={handleDismissMessage}
						spacing="compact"
					/>
				</Inline>
			</Box>
			<FireScreenAnalytics />
		</ContextualAnalyticsData>
	) : null;
};

const boxStyles = xcss({
	borderRadius: 'radius.small',
});

const helpTextStyles = xcss({
	alignSelf: 'center',
});
